import { CSVExporter } from "@/components/pure/csv-expoter/csv-exporter";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";
import { message } from "antd";
import { type FC, useEffect, useState } from "react";

interface LocationExporterProps {
	triggerExport: boolean;
	searchVal?: string;
	onExportComplete: () => void;
}

// Define the specific fields to export in order
const exportFields = [
	"id",
	"name",
	"store_number",
	"address1",
	"city",
	"state",
	"zip",
	"submission_count",
	"health_score",
];

// Define custom column header mappings
const customHeaderMap: { [key: string]: string } = {
	id: "ID",
	name: "SITE NAME",
	store_number: "STORE NUMBER",
	address1: "ADDRESS",
	city: "CITY",
	state: "STATE",
	zip: "ZIP",
	submission_count: "ASSET COUNT",
	health_score: "HEALTH SCORE",
};

export const LocationExporter: FC<LocationExporterProps> = ({
	triggerExport,
	searchVal,
	onExportComplete,
}) => {
	const [csvData, setCsvData] = useState<any[] | null>(null);

	useEffect(() => {
		if (!triggerExport) return;

		const handleExport = async () => {
			message.info("Exporting locations...");

			try {
				// Fetch all locations for the organization
				const url = `${VITE_CORE_API_URL}/core/location`;
				const response = await fetch(url, {
					method: "GET",
					headers: {
						Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
						"Content-Type": "application/json",
					},
				});

				if (!response.ok) {
					throw new Error(`Failed to fetch locations (${response.status})`);
				}

				const result = await response.json();
				let allLocations = result.data || [];

				if (!Array.isArray(allLocations) || allLocations.length === 0) {
					message.error("No locations found to export");
					onExportComplete();
					return;
				}

				// Apply search filter if present (to match what's shown in the table)
				if (searchVal?.trim()) {
					const searchLower = searchVal.toLowerCase();
					allLocations = allLocations.filter(
						(location: any) =>
							location.name?.toLowerCase().includes(searchLower) ||
							location.store_number?.toLowerCase().includes(searchLower) ||
							location.address1?.toLowerCase().includes(searchLower) ||
							location.city?.toLowerCase().includes(searchLower) ||
							location.state?.toLowerCase().includes(searchLower),
					);
				}

				if (allLocations.length === 0) {
					message.error("No locations found matching the search criteria");
					onExportComplete();
					return;
				}

				// Transform the data for CSV export - flatten nested health_score
				const transformedLocations = allLocations.map((location: any) => ({
					...location,
					health_score: location.health_score?.health_score ?? null,
				}));

				setCsvData(transformedLocations);
			} catch (error) {
				console.error("Error exporting locations:", error);
				message.error("Failed to export locations. Please try again.");
				onExportComplete();
			}
		};

		handleExport();
	}, [triggerExport, searchVal, onExportComplete]);

	// Reset csvData after export
	const handleExportComplete = () => {
		setCsvData(null);
		onExportComplete();
	};

	if (!csvData || csvData.length === 0) return null;

	return (
		<CSVExporter
			data={csvData}
			filename="locations-export.csv"
			autoDownload
			fields={exportFields}
			customHeaderMap={customHeaderMap}
			onComplete={handleExportComplete}
		/>
	);
};

